import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export type Transcript = {
  id: string;
  source: "fireflies" | "plaud" | "otter" | "google_meet" | string;
  source_id: string;
  title: string | null;
  ai_title: string | null;
  summary: string | null;
  action_items: string[] | null;
  speakers: string[] | null;
  meeting_date: string | null;
  duration_seconds: number | null;
  created_at: string;
};

export type TranscriptDetail = Transcript & {
  transcript_text: string | null;
  metadata: Record<string, unknown> | null;
};

/** Display name: AI title wins, then the source title, then a dated fallback. */
export function transcriptDisplayTitle(t: Pick<Transcript, "ai_title" | "title" | "meeting_date">): string {
  if (t.ai_title?.trim()) return t.ai_title.trim();
  if (t.title?.trim()) return t.title.trim();
  return t.meeting_date
    ? `Recording — ${new Date(t.meeting_date).toLocaleDateString()}`
    : "Untitled recording";
}

// Plaud's auto-summaries arrive as raw markdown (headers, blockquotes,
// embedded ![PLAUD NOTE](...) images, even <mark> HTML). The app has no
// markdown renderer, so strip the decoration down to readable plain text.
export function cleanSummaryText(raw: string | null): string | null {
  if (!raw) return null;
  const cleaned = raw
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // markdown images
    .replace(/\[([^\]]*)\]\(([^)]*)\)/g, "$1") // links → label
    .replace(/<[^>]+>/g, "") // html tags
    .replace(/^[ \t]*#{1,6}[ \t]*/gm, "") // headers
    .replace(/^[ \t]*>[ \t]?/gm, "") // blockquotes
    .replace(/\*\*([^*]*)\*\*/g, "$1") // bold
    .replace(/^[ \t]*-{3,}[ \t]*$/gm, "") // hr lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return cleaned || null;
}

export function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m}m`;
}

// List intentionally skips transcript_text — full text only loads on the
// detail query so the library view stays light.
export function useTranscripts(search: string, source: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["transcripts", user?.id, search, source],
    queryFn: async () => {
      let q = supabase
        .from("meeting_transcripts")
        .select(
          "id, source, source_id, title, ai_title, summary, action_items, speakers, meeting_date, duration_seconds, created_at",
        )
        .eq("user_id", user!.id)
        .order("meeting_date", { ascending: false, nullsFirst: false })
        .limit(200);
      if (source) q = q.eq("source", source);
      if (search.trim()) {
        const term = search.trim().replaceAll("%", "\\%").replaceAll(",", " ");
        q = q.or(`title.ilike.%${term}%,ai_title.ilike.%${term}%,summary.ilike.%${term}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as Transcript[];
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useTranscript(id: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["transcript", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("meeting_transcripts")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as TranscriptDetail;
    },
    enabled: !!user && !!id,
  });
}
